import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Workflow, Package, MapPin, ArrowRight, ShieldCheck,
  ClipboardCheck, QrCode, Nfc, Thermometer,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ChainOfCustodyTimeline from '../components/ChainOfCustodyTimeline';
import ChainStatusBadge from '../components/ChainStatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Chain of Custody — capability showcase page.
 *
 * Discoverable from the main sidebar. Always shows the end-to-end
 * traceability story (live when batches exist, otherwise a clearly
 * labelled DEMO sequence). Lists registered batches as deep links into
 * the per-batch operational timeline.
 */
const ChainOfCustody = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [correctionCounts, setCorrectionCounts] = useState({});
  const [showcase, setShowcase] = useState(null); // {batch, events, corrections}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('mvp_token');
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      try {
        const [bRes, mcRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/batches`).catch(() => ({ data: [] })),
          axios
            .get(`${BACKEND_URL}/api/manual-corrections/counts`, cfg)
            .catch(() => ({ data: {} })),
        ]);
        const activeBatches = bRes.data || [];
        const counts = mcRes.data || {};
        setBatches(activeBatches);
        setCorrectionCounts(counts);

        // ----- Hydrate the showcase timeline from the most-advanced
        // active batch (Option A). We fetch events for every active
        // batch in parallel, pick the one with the highest recorded
        // stage count (ties broken by most-recent activity timestamp),
        // then materialise the showcase. If zero active batches exist
        // we leave showcase=null so the original empty-state renders.
        if (activeBatches.length > 0) {
          const eventsByBatch = await Promise.all(
            activeBatches.map((b) =>
              axios
                .get(`${BACKEND_URL}/api/batches/${b.id}/events`)
                .then((r) => ({ batch: b, events: r.data || [] }))
                .catch(() => ({ batch: b, events: [] })),
            ),
          );

          // "Advancement" score:
          //   primary  = count of distinct canonical stages reached
          //   tiebreak = max(timestamp) of any event on the batch
          const score = ({ events }) => {
            const stageKeys = new Set();
            let latest = '';
            for (const ev of events) {
              const k = (ev.stage_key || ev.event_type || '')
                .toLowerCase()
                .replace(/\s+/g, '_');
              if (k) stageKeys.add(k);
              const ts = ev.timestamp || '';
              if (ts > latest) latest = ts;
            }
            return { stages: stageKeys.size, latest };
          };

          const ranked = eventsByBatch
            .map((row) => ({ ...row, _score: score(row) }))
            .sort((a, b) => {
              if (b._score.stages !== a._score.stages) {
                return b._score.stages - a._score.stages;
              }
              return (b._score.latest || '').localeCompare(a._score.latest || '');
            });

          const winner = ranked[0];
          if (winner && winner._score.stages > 0) {
            // Pull corrections for the winning batch so manually-resolved
            // stages render with the amber Verified Operator Resolution
            // theme (same shape used by BatchDetail + Compliance).
            const correctionsRes = await axios
              .get(`${BACKEND_URL}/api/batches/${winner.batch.id}/manual-corrections`, cfg)
              .catch(() => ({ data: [] }));
            setShowcase({
              batch: winner.batch,
              events: winner.events,
              corrections: correctionsRes.data || [],
            });
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="chain-of-custody-page">
      {/* Header — operational page title only; the timeline below already
          demonstrates the 11-stage canonical model. */}
      <div data-testid="coc-page-header">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Workflow className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800"
            style={{ fontFamily: 'Manrope' }}
          >
            Chain of Custody
          </h1>
        </div>
        <p
          className="text-sm sm:text-base text-slate-600 max-w-3xl leading-relaxed"
          data-testid="coc-page-lead"
        >
          Enterprise traceability framework spanning 11 canonical stages from
          Hatchery to Consumer Verification.
        </p>

        {/* Compact capability strip — one line, no marketing copy */}
        <div
          className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5"
          data-testid="coc-capability-strip"
          aria-label="Connected platform capabilities"
        >
          <CapabilityChip icon={ClipboardCheck} label="Compliance" />
          <CapabilityChip icon={ShieldCheck}    label="Audit Trail" />
          <CapabilityChip icon={QrCode}         label="QR Verification" />
          <CapabilityChip icon={Nfc}            label="NFC Verification" />
          <CapabilityChip icon={Thermometer}    label="Cold Chain Monitoring" />
        </div>
      </div>

      {/* Showcase timeline — hydrated from the most-advanced active batch
          when one exists (Option A). Empty-state is preserved verbatim
          when zero active batches exist or no batch has any events yet. */}
      <section
        className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 lg:p-8"
        data-testid="chain-of-custody-showcase"
      >
        {showcase && (
          <div
            className="mb-3 sm:mb-4 flex items-center gap-2 flex-wrap"
          >
            <span
              data-testid="coc-showcase-attribution"
              className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-[10px] sm:text-xs font-semibold text-emerald-800"
            >
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Showing live batch: {showcase.batch.batch_number || showcase.batch.id}
            </span>
            <button
              type="button"
              data-testid="coc-showcase-view-full-batch"
              onClick={() => navigate(`/mvp/batches/${showcase.batch.id}`)}
              className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline decoration-emerald-300 hover:decoration-emerald-600 underline-offset-2"
            >
              View full batch
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
        <ChainOfCustodyTimeline
          events={showcase?.events || []}
          corrections={showcase?.corrections || []}
        />
      </section>

      {/* Batches list */}
      <section data-testid="chain-of-custody-batches">
        <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
          <h2
            className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800"
            style={{ fontFamily: 'Manrope' }}
          >
            Per-Batch Operational Timelines
          </h2>
        </div>
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm text-slate-500">
            Loading registered batches…
          </div>
        ) : batches.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 sm:p-8 text-center">
            <Package className="w-7 h-7 text-slate-400 mx-auto mb-3" />
            <p className="text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
              No batches are registered yet. The first registered batch will
              appear here with its own real-time chain-of-custody timeline.
              The demonstration above shows what the experience looks like
              once operational events stream in.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {batches.map((b) => {
              const mcCount = correctionCounts[b.id] || 0;
              const hasCorrections = mcCount > 0;
              return (
              <button
                key={b.id}
                onClick={() => navigate(`/mvp/batches/${b.id}`)}
                data-testid={`coc-batch-card-${b.id}`}
                className="text-left bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition p-4 sm:p-5"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-mono text-slate-500 truncate">
                      {b.batch_number || b.id}
                    </p>
                    <p
                      className="text-base sm:text-lg font-semibold text-slate-900 truncate"
                      style={{ fontFamily: 'Manrope' }}
                    >
                      {b.product_name || 'Unnamed product'}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-600 italic truncate">
                      {b.species || '—'}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap text-xs sm:text-sm">
                  <span className="text-slate-600 flex items-center gap-1.5 min-w-0">
                    <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{b.origin?.name || '—'}</span>
                  </span>
                  <span className="flex items-center gap-1.5 flex-wrap justify-end"
                        onClick={(e) => e.stopPropagation()}
                  >
                    <ChainStatusBadge batchId={b.id} compact />
                  <span
                    data-testid={`coc-batch-correction-indicator-${b.id}`}
                    data-corrections={mcCount}
                    title={
                      hasCorrections
                        ? `${mcCount} verified operator resolution${mcCount === 1 ? '' : 's'} on this batch — full audit trail available.`
                        : 'No manual corrections logged — every recorded event was live-captured.'
                    }
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border whitespace-nowrap text-[10px] sm:text-xs font-semibold cursor-help ${
                      hasCorrections
                        ? 'bg-amber-50 border-amber-300 text-amber-800'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`w-1.5 h-1.5 rounded-full ${
                        hasCorrections ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                    />
                    {mcCount} Correction{mcCount === 1 ? '' : 's'}
                  </span>
                  </span>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

const CapabilityChip = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-[11px] sm:text-xs font-medium text-slate-700">
    <Icon className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" aria-hidden="true" />
    {label}
  </span>
);

export default ChainOfCustody;
